import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield, Clock, AlertTriangle, CheckCircle, XCircle, FileText, Activity } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Skeleton } from '../../components/ui/Skeleton';
import { useConsent, useConsentAccessLog, useRevokeConsent } from '../../hooks/useOpenFinance';
import { useState } from 'react';

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'error' | 'default' }> = {
  AWAITING_AUTHORIZATION: { label: 'Aguardando', variant: 'warning' },
  AUTHORIZED: { label: 'Autorizado', variant: 'success' },
  REJECTED: { label: 'Rejeitado', variant: 'error' },
  REVOKED: { label: 'Revogado', variant: 'error' },
  EXPIRED: { label: 'Expirado', variant: 'default' },
};

const scopeLabels: Record<string, string> = {
  ACCOUNTS: 'Contas',
  TRANSACTIONS: 'Transações',
  CARDS: 'Cartões',
  CREDIT: 'Crédito',
};

export function ConsentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');

  const { data: consent, isLoading } = useConsent(id || '');
  const { data: accessLogs, isLoading: isLoadingLogs } = useConsentAccessLog(id || '');
  const revokeMutation = useRevokeConsent();

  const handleRevoke = async () => {
    if (!id) return;
    try {
      await revokeMutation.mutateAsync({ consentId: id, reason: revokeReason });
      setShowRevokeConfirm(false);
      navigate('/banking/consents');
    } catch (error) {
      console.error('Erro ao revogar consentimento:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!consent) {
    return (
      <div className="text-center py-12">
        <Shield className="w-12 h-12 text-surface-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-surface-100 mb-2">Consentimento não encontrado</h2>
        <Button variant="secondary" onClick={() => navigate('/banking/consents')}>
          Voltar
        </Button>
      </div>
    );
  }

  const status = statusConfig[consent.status] || { label: consent.status, variant: 'default' as const };
  const canRevoke = consent.status === 'AUTHORIZED';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" size="sm" onClick={() => navigate('/banking/consents')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Detalhes do Consentimento</h1>
          <p className="text-surface-400">Gerencie o compartilhamento de dados</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Informações do Consentimento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-surface-400">Status</span>
                <Badge variant={status.variant}>{status.label}</Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-surface-400">Instituição (TPP)</span>
                <div className="flex items-center gap-2">
                  {consent.tppLogo && (
                    <img src={consent.tppLogo} alt={consent.tppName} className="w-6 h-6 rounded" />
                  )}
                  <span className="text-surface-100 font-medium">{consent.tppName}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-surface-400">Tipo</span>
                <span className="text-surface-100">
                  {consent.type === 'DATA_SHARING' ? 'Compartilhamento de Dados' : 'Iniciação de Pagamento'}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-surface-400">Criado em</span>
                <span className="text-surface-100">
                  {new Date(consent.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-surface-400">Expira em</span>
                <span className="text-surface-100">
                  {new Date(consent.expiresAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                  })}
                </span>
              </div>

              {consent.authorizedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-400">Autorizado em</span>
                  <span className="text-surface-100">
                    {new Date(consent.authorizedAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}

              {consent.revokedAt && (
                <div className="flex items-center justify-between">
                  <span className="text-surface-400">Revogado em</span>
                  <span className="text-surface-100">
                    {new Date(consent.revokedAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Dados Compartilhados
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {consent.scopes.map((scope) => (
                  <Badge key={scope} variant="primary">
                    {scopeLabels[scope] || scope}
                  </Badge>
                ))}
              </div>
              {consent.permissions && consent.permissions.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-surface-400 mb-2">Permissões específicas:</p>
                  <ul className="text-sm text-surface-300 space-y-1">
                    {consent.permissions.map((perm, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {perm}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Histórico de Acesso
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingLogs ? (
                <div className="space-y-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : accessLogs && accessLogs.length > 0 ? (
                <div className="space-y-3">
                  {accessLogs.slice(0, 10).map((log) => (
                    <div
                      key={log.id}
                      className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg"
                    >
                      <div>
                        <p className="text-sm text-surface-100">{log.dataType}</p>
                        <p className="text-xs text-surface-400">{log.endpoint}</p>
                      </div>
                      <div className="text-right">
                        <Badge variant={log.responseCode === 200 ? 'success' : 'error'}>
                          {log.responseCode}
                        </Badge>
                        <p className="text-xs text-surface-400 mt-1">
                          {new Date(log.requestedAt).toLocaleString('pt-BR')}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-surface-400 text-center py-4">Nenhum acesso registrado</p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ações</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {canRevoke && !showRevokeConfirm && (
                <Button
                  variant="secondary"
                  className="w-full text-red-400 hover:text-red-300"
                  onClick={() => setShowRevokeConfirm(true)}
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Revogar Consentimento
                </Button>
              )}

              {showRevokeConfirm && (
                <div className="space-y-3 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span className="font-medium">Confirmar Revogação</span>
                  </div>
                  <p className="text-sm text-surface-300">
                    Ao revogar, a instituição {consent.tppName} não poderá mais acessar seus dados.
                  </p>
                  <textarea
                    className="w-full px-3 py-2 bg-surface-800 border border-surface-700 rounded-lg text-surface-100 text-sm"
                    placeholder="Motivo da revogação (opcional)"
                    rows={2}
                    value={revokeReason}
                    onChange={(e) => setRevokeReason(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1"
                      onClick={() => setShowRevokeConfirm(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1 bg-red-600 hover:bg-red-700"
                      onClick={handleRevoke}
                      disabled={revokeMutation.isPending}
                    >
                      {revokeMutation.isPending ? 'Revogando...' : 'Confirmar'}
                    </Button>
                  </div>
                </div>
              )}

              {!canRevoke && (
                <p className="text-sm text-surface-400 text-center">
                  {consent.status === 'REVOKED'
                    ? 'Este consentimento já foi revogado'
                    : consent.status === 'EXPIRED'
                    ? 'Este consentimento expirou'
                    : 'Nenhuma ação disponível'}
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Validade
              </CardTitle>
            </CardHeader>
            <CardContent>
              {consent.status === 'AUTHORIZED' && (
                <>
                  <div className="text-center mb-4">
                    <p className="text-3xl font-bold text-surface-100">
                      {Math.max(
                        0,
                        Math.ceil(
                          (new Date(consent.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
                        )
                      )}
                    </p>
                    <p className="text-surface-400">dias restantes</p>
                  </div>
                  <div className="w-full bg-surface-700 rounded-full h-2">
                    <div
                      className="bg-primary-500 h-2 rounded-full"
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(
                            100,
                            ((new Date(consent.expiresAt).getTime() - Date.now()) /
                              (new Date(consent.expiresAt).getTime() -
                                new Date(consent.createdAt).getTime())) *
                              100
                          )
                        )}%`,
                      }}
                    />
                  </div>
                </>
              )}
              {consent.status !== 'AUTHORIZED' && (
                <p className="text-surface-400 text-center">Consentimento inativo</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
